<mat-card class="card-table has-progress-bar">
  <mat-progress-bar *ngIf="dataSource.loading$ | async" mode="indeterminate"></mat-progress-bar>
  <mat-card-title
    *ngIf="options.filtering && showFilters"
    fxLayout="row"
    fxLayoutGap="1rem"
    fxLayoutAlign="space-between center"
  >
    <ng-content></ng-content>
  </mat-card-title>
  <mat-card-content>
    <table
      mat-table
      [dataSource]="dataSource"
      matSort
      [matSortActive]="options.sortActive"
      [matSortDirection]="options.sortDirection"
      matSortDisableClear
      (matSortChange)="sortChange.next($event)"
      class="mat-table--wide"
      [class.mat-table--actionable]="options.rowClickable"
      [class.mat-table--hover]="options.rowClickable"
      [class.sticky-last-column]="options.stickyLastColumn"
    >
      <ng-container
        [matColumnDef]="column.key"
        *ngFor="let column of columns; let i = index"
      >
        <ng-container *ngIf="column.canSort; else noSort">
          <th mat-header-cell *matHeaderCellDef [mat-sort-header]="column.key">
            {{ column.verboseName | translate }}
          </th>
        </ng-container>
        <ng-template #noSort>
          <th mat-header-cell *matHeaderCellDef>
            {{ column.verboseName | translate }}
          </th>
        </ng-template>
        <td
          mat-cell
          [style.maxWidth.px]="options?.maxCellWidth"
          [style.minWidth.px]="options?.minCellWidth"
          *matCellDef="let element"
        >
          <!-- Begin cell output -->
          <ng-container
            *ngIf="templateForColumn(column) as cellTemplate; else noTemplate"
          >
            <!-- With template outlet -->
            <ng-container
              *ngTemplateOutlet="
                cellTemplate.template;
                context: {
                  record: element,
                  $implicit: column.propertyAccessor
                    ? column.propertyAccessor(
                        _get(element, column.key),
                        element
                      )
                    : _get(element, column.key)
                }
              "
            ></ng-container>
          </ng-container>
          <ng-template #noTemplate>
            <ng-template #WithPropAccessor>
              <!-- With property accessor -->
              <ng-container
                *ngIf="
                  column.propertyAccessor(
                    element[column.key],
                    element
                  ) as value;
                  else none
                "
              >
                <ng-container
                  *ngTemplateOutlet="
                    propTypeOutput;
                    context: {
                      value: value,
                      column: column,
                      record: element
                    }
                  "
                ></ng-container>
              </ng-container>
            </ng-template>

            <ng-container
              *ngIf="!column.propertyAccessor; else WithPropAccessor"
            >
              <div fxLayout="column">
                <ng-container
                  *ngTemplateOutlet="
                    propTypeOutput;
                    context: {
                      value: _get(element, column.key),
                      column: column,
                      record: element
                    }
                  "
                ></ng-container>
                <ng-container *ngIf="column.secondary">
                  <span class="text-secondary text-sm">{{
                    _get(element, column.secondary)
                  }}</span>
                </ng-container>
              </div>
            </ng-container>
          </ng-template>
        </td>
      </ng-container>

      <tr
        [ngClass]="[options.styles.tableHeaderClass]"
        mat-header-row
        *matHeaderRowDef="displayedColumns; sticky: options.styles.stickyHeader"
      ></tr>

      <tr
        mat-row
        (click)="rowClicked(row)"
        [style.height]="options.styles.tableRowHeight"
        class="{{
          selection.isSelected(row) ? options.styles.selectedRowClass : ''
        }}"
        [class.selectable]="options.selection"
        [ngClass]="options.rowClass ? options.rowClass(row) : []"
        [class.fade-in]="row.newRecord"
        *matRowDef="let row; columns: displayedColumns"
      ></tr>
    </table>

    <ng-container *ngIf="dataSource.empty$ | async">
      <div class="ngx-mat-table-empty-data">
        <ng-container *ngIf="noDataTemplate; else emptyDataTmp">
          <ng-container *ngTemplateOutlet="noDataTemplate"></ng-container>
        </ng-container>
      </div>
    </ng-container>

    <ng-container *ngIf="dataSource.loading$ | async">
      <div class="ngx-mat-table-loading-data">
        <ng-container *ngIf="loadingTemplate">
          <ng-container *ngTemplateOutlet="loadingTemplate"></ng-container>
        </ng-container>
        <ng-container *ngIf="!loadingTemplate">
          <div fxLayout="row" class="mt-6">
            <mat-spinner
              mode="indeterminate"
              class="ml-6 mr-2"
              [diameter]="20"
            ></mat-spinner>
            <strong>{{ options.loadingText | translate }}...</strong>
          </div>
        </ng-container>
      </div>
    </ng-container>

    <div class="pagination-reset-container" *ngIf="options.paging">
      <div
        class="mat-paginator-outer-container pagination-refresh-container"
        *ngIf="dataSource.bust$ | async"
      >
        <div class="mat-paginator-container">
          <button
            class="pagination-refresh"
            mat-stroked-button
            type="button"
            (click)="reloadPagination()"
          >
            <mat-icon>refresh</mat-icon>
            <span>{{ 'paginator.reload' | translate }}</span>
          </button>
        </div>
      </div>
      <mat-paginator
        [length]="dataSource.collectionTotal$ | async"
        [pageSize]="options.defaultPageSize"
        [pageSizeOptions]="[10, 15, 20, 50, 100]"
        [hidePageSize]="dataSource.notPaged$ | async"
        [disabled]="
          (dataSource.notPaged$ | async) || (dataSource.bust$ | async)
        "
        (page)="pageChange.next($event)"
      >
      </mat-paginator>
    </div>
  </mat-card-content>
</mat-card>

<ng-template
  #propTypeOutput
  let-column="column"
  let-value="value"
  let-record="record"
>
  <ng-container [ngSwitch]="column.propertyType">
    <span *ngSwitchCase="'Array'" [title]="value">
      <ng-container *ngFor="let val of value; let i = index">
        {{ val[column.secondaryKey] }}{{i === value.length -1 ? '' : ',&nbsp;' }}
      </ng-container>
    </span>
    <span *ngSwitchCase="'String'" [title]="value">
      {{ (value | translate) || (options.placeholder | translate) }}
    </span>
    <span *ngSwitchCase="'RouterLink'">
      <a [routerLink]="column.link(record)">{{
        value ||
          (column.placeholder | translate) ||
          (options.placeholder | translate)
      }}</a>
    </span>
    <span *ngSwitchCase="'Link'">
      <a [href]="column.link(record)">{{
        value ||
          (column.placeholder | translate) ||
          (options.placeholder | translate)
      }}</a>
    </span>
    <span *ngSwitchCase="'Number'">
      <ng-container *ngIf="column.format">
        {{
          (value | normalizeDecimal | number: column.format) ||
            (column.placeholder | translate) ||
            (options.placeholder | translate)
        }}
      </ng-container>
      <ng-container *ngIf="!column.format">
        {{
          value ||
            (column.placeholder | translate) ||
            (options.placeholder | translate)
        }}
      </ng-container>
    </span>
    <span *ngSwitchCase="'Date'">
      <ng-container *ngIf="column.format; else momentDate">
        {{
          (value | date: column.format) ||
            (column.placeholder | translate) ||
            (options.placeholder | translate)
        }}
      </ng-container>
      <ng-template #momentDate>
        {{
          (value | momentDate: 'utc') ||
            (column.placeholder | translate) ||
            (options.placeholder | translate)
        }}
      </ng-template>
    </span>
    <span *ngSwitchCase="'DateTime'">
      <span>
        {{
          (value | momentDateTime: 'local') ||
            (column.placeholder | translate) ||
            (options.placeholder | translate)
        }}
      </span>
    </span>
    <span *ngSwitchCase="'Currency'">
      {{
        (value | currency: column.format(record)) ||
          (column.placeholder | translate) ||
          (options.placeholder | translate)
      }}
    </span>
    <span *ngSwitchCase="'Telephone'">
      <a *ngIf="value" href="tel:{{ value }}">{{ value }}</a>
      <!-- TODO: use phone pipe -->
      <ng-container *ngIf="!value">{{
        (column.placeholder | translate) || (options.placeholder | translate)
      }}</ng-container>
    </span>
    <span *ngSwitchCase="'Email'">
      <a *ngIf="value" href="mailto:{{ value }}">{{ value }}</a>
      <ng-container *ngIf="!value">{{
        (column.placeholder | translate) || (options.placeholder | translate)
      }}</ng-container>
    </span>
    <span *ngSwitchDefault>
      <ng-container *ngIf="column.propertyAccessor">
        <span [innerHTML]="value | translate"></span>
      </ng-container>
      <ng-container *ngIf="!column.propertyAccessor">
        {{
          (value | translate) ||
            (column.placeholder | translate) ||
            (options.placeholder | translate)
        }}
      </ng-container>
      <!-- Default type -->
    </span>
  </ng-container>
</ng-template>

<ng-template #emptyDataTmp>
  <div class="no-data mt-6 ml-6" fxLayout="row" fxLayoutAlign="start center">
    <mat-icon color="warn">warning</mat-icon>
    <span style="padding-left: 5px">
      {{ options.emptyDataText | translate }}
    </span>
  </div>
</ng-template>
